import { useState, useEffect } from "react";
import { X, Download, Smartphone } from "lucide-react";
import { Button } from "./ui/button";

export const InstallPWA = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);
    
    if (standalone) return;

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(ios);

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // For iOS, show custom prompt
    if (ios) {
      setTimeout(() => setShowPrompt(true), 3000);
      return;
    }

    // For Android/Desktop, listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-96 bg-neutral-950 text-white p-4 rounded-lg shadow-2xl z-50 border border-zinc-800"
      data-testid="install-pwa-prompt"
    >
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-zinc-500 hover:text-white"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        
        <div className="flex-1">
          <h3 
            className="font-bold text-lg mb-1"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Installa l'App
          </h3>
          
          {isIOS ? (
            <p className="text-sm text-zinc-400 mb-3">
              Tocca <span className="inline-flex items-center mx-1 px-1 bg-zinc-800 rounded">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L12 14M12 14L8 10M12 14L16 10M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </span> e poi <strong>"Aggiungi a Home"</strong>
            </p>
          ) : (
            <>
              <p className="text-sm text-zinc-400 mb-3">
                Installa l'app per accedere rapidamente al catalogo auto
              </p>
              <Button 
                onClick={handleInstall}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Installa
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
